import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ALL_PRODUCTS as LOCAL_PRODUCTS } from '@/data/catalog';

// Standard size ladder auto-applied to open-edition prints at $45.
// Individual products can override by setting their own `sizes` array.
const DEFAULT_PRINT_SIZES = [
  { label: '5×7', price: 25 },
  { label: '8×10', price: 45 },
  { label: '11×14', price: 75 },
];

// Limited-edition sizes (larger, higher price point).
const LIMITED_EDITION_SIZES = [
  { label: '8×10', price: 200 },
  { label: '11×14', price: 325 },
];

const enrichProduct = (p) => {
  if (p.sizes && p.sizes.length) return p; // explicit sizes win
  if (p.originalCategory === 'prints' && p.price === 45) {
    return { ...p, sizes: DEFAULT_PRINT_SIZES };
  }
  if (p.originalCategory === 'limited-edition-prints' && p.price === 200) {
    return { ...p, sizes: LIMITED_EDITION_SIZES };
  }
  return p;
};

const enrichAll = (list) => list.map(enrichProduct);

const ProductsContext = createContext(null);

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return ctx;
};

export const ProductsProvider = ({ children }) => {
  // Hydrate immediately from the bundled local catalog so the UI never flashes empty.
  // Then replace with the live API result when it arrives.
  const [products, setProducts] = useState(() => enrichAll(LOCAL_PRODUCTS));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('local');

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/products`);
        if (!res.ok) throw new Error(`Failed to load products (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setProducts(enrichAll(data));
          setSource('api');
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Falling back to bundled catalog:', err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const bySlug = new Map(products.map((p) => [p.slug, p]));
    return {
      products,
      loading,
      error,
      source,
      getProductBySlug: (slug) => bySlug.get(slug),
      getProductsByCollection: (collectionSlug) =>
        products.filter((p) => p.collection === collectionSlug),
    };
  }, [products, loading, error, source]);

  return (
    <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>
  );
};

export default ProductsProvider;
